import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Collapse,
  Hidden,
  IconButton,
  MenuItem,
  Typography,
} from "@mui/material";
import {
  AddBox,
  OpenInNewRounded,
  ChevronLeftRounded,
  ChevronRightRounded,
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import MaterialTable from "material-table";
import { MTableToolbar } from "material-table";
import { useSnackbar } from "notistack";
import axios from "axios";
import { getMediaURL, scrollTop } from "../../../../config/values";
import { TableCount, TablePage } from "../../../../config/Permissions";
import { getDataTableLocalization } from "../../../../config/Translate";
import { withStyles } from "@mui/styles";
import { Styles } from "../../../../config/design/styles";
import { tableIcons } from "../../../../config/design/TableIcons";
import { defaultVariables } from "../../../../config";
import { useIsSmall } from "../../../../config/hooks/useWindowDimensions";
import { useTranslation } from "react-i18next";
import { Wallet } from "lucide-react";
import RayaField from "../../../components/elements/fields/RayaField";
import * as XLSX from "xlsx";

const Shops = ({ classes }) => {
  const isSmall = useIsSmall();
  const { t } = useTranslation();

  const [columns] = useState([
    {
      field: "logo",
      editable: "never",
      filtering: false,
      sorting: false,
      hidden: isSmall,
      title: t("logo"),
      render: (rowData) =>
        !!rowData.logo && (
          <img alt="avatar" src={rowData.logo} className={classes.avatar} />
        ),
    },
    {
      title: t("shopName"),
      field: "name",
      filterPlaceholder: t("search"),
      sorting: false,
    },
    {
      title: t("industry"),
      field: "industry",
      filterPlaceholder: t("search"),
      sorting: false,
    },
    {
      title: "Teslimat",
      editable: "never",
      field: "transfer_by",
      sorting: false,
      filterPlaceholder: t("choose"),
      render: (rowData) => {
        if (rowData.transfer_by === "system") return "kolaydoy";
        if (rowData.transfer_by === "shop") return "kendisi";
        return "-";
      },
    },
    {
      title: "Komisyon",
      editable: "never",
      field: "commission",
      sorting: false,
      render: (rowData) =>
        rowData.commission !== null && rowData.commission !== undefined ?
          `${rowData.commission}%`
        : "-",
    },
    {
      title: t("status"),
      editable: "never",
      field: "status",
      sorting: false,
      filterPlaceholder: t("choose"),
      lookup: {
        active: (
          <Typography className="table-badge active">{t("active")}</Typography>
        ),
        pending: (
          <Typography className="table-badge pending">
            {t("pending")}
          </Typography>
        ),
        inactive: (
          <Typography className="table-badge inactive">
            {t("inactive")}
          </Typography>
        ),
      },
    },
    {
      title: t("activity"),
      editable: "never",
      field: "activity",
      sorting: false,
      render: (rowData) => (
        <>
          <Button
            variant="outlined"
            color="secondary"
            component={Link}
            to={`/panel/manage/shops/edit/${rowData.id}`}
            endIcon={<ChevronRightRounded />}
            size="small"
            style={{ margin: 5 }}
          >
            {t("details")}
          </Button>
          {!!rowData.wallet_id && (
            <IconButton
              component={Link}
              size="small"
              to={`/panel/manage/wallets/transactions/${rowData.wallet_id}`}
            >
              <Wallet />
            </IconButton>
          )}
        </>
      ),
    },
  ]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [industries, setIndustries] = useState([]);
  const [filters, setFilters] = useState({
    transfer_by: "all",
    status: "all",
    industry_id: "all",
  });
  const tableRef = useRef(null);
  const searchRef = useRef("");
  const { enqueueSnackbar } = useSnackbar();
  const loadData = () => {
    tableRef.current && tableRef.current.onQueryChange();
  };

  useEffect(() => {
    const fetchIndustries = async () => {
      try {
        const response = await axios.get(
          `${defaultVariables.url}/industries/get-all`,
          {
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${localStorage.getItem("ACT")}`,
            },
          },
        );
        setIndustries(response.data || []);
      } catch (error) {
        console.log(error.response);
      }
    };
    fetchIndustries();
  }, []);

  const onDelete = async (id, hasNotif = true) => {
    try {
      const loginToken = localStorage.getItem("ACT");
      let data = new FormData();
      data.append("id", id);
      await axios.post(`${defaultVariables.url}/shops/remove`, data, {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${loginToken}`,
        },
      });
      if (hasNotif) enqueueSnackbar(t("deleteData"), { variant: "success" });
      loadData();
    } catch (error) {
      console.log(error.response);
      enqueueSnackbar(t("deleteDataFailed"), { variant: "error" });
    }
  };

  const handleExportXlsx = async () => {
    try {
      setExporting(true);
      const response = await axios.get(`${defaultVariables.url}/shops/quickView`, {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${localStorage.getItem("ACT")}`,
        },
        params: {
          page: 1,
          per_page: 10000,
          search: searchRef.current || "",
          status: filters.status !== "all" ? filters.status : undefined,
          transfer_by: filters.transfer_by !== "all" ? filters.transfer_by : undefined,
          industry_id: filters.industry_id !== "all" ? filters.industry_id : undefined,
        },
      });

      const exportRows = (response.data?.data || []).map((row) => ({
        "Shop ID": row.id || "-",
        "Restoran / Satıcı adı": row.name || "-",
        Slug: row.slug || "-",
        Sektör: t(row.industry?.name) || "-",
        "Sektör ID": row.industry_id || "-",
        "Kurye / Teslimat": row.transfer_by === "system" ? "kolaydoy" : row.transfer_by === "shop" ? "kendisi" : "-",
        "Komisyon (%)": row.commission ?? "-",
        Durum: row.status ? t(row.status) : "-",
        "Açıklama (Shop)": row.description || "-",
        "Minimum Sipariş": row.minimum_order ?? "-",
        "Hazırlık Süresi (dk)": row.preparation_time ?? "-",
        "Ücretsiz Teslimat": row.has_free_delivery ? "Evet" : "Hayır",
        "Ücretsiz Teslimat Limiti": row.free_delivery_limit ?? "-",
        "Servis Yarıçapı": row.radius ?? "-",
        "Popüler": row.is_popular ? "Evet" : "Hayır",
        "Ekonomik": row.is_economic ? "Evet" : "Hayır",
        "Kapıda Ödeme": row.is_pod ? "Evet" : "Hayır",
        "Ortalama Puan": row.average_rate ?? "-",
        "Shop Oluşturulma": row.created_at || "-",
        "Shop Güncellenme": row.updated_at || "-",
        "Yönetici ID": row.owner?.id || "-",
        "Yönetici Ad": row.owner?.first_name || "-",
        "Yönetici Soyad": row.owner?.last_name || "-",
        "Yönetici Ad Soyad": [row.owner?.first_name, row.owner?.last_name].filter(Boolean).join(" ") || "-",
        "Yönetici E-posta": row.owner?.email || "-",
        "Yönetici Telefon": row.owner?.mobile || "-",
        "Yönetici Rol ID": row.owner?.role_id || "-",
        "Yönetici Cüzdan ID": row.owner?.wallet?.id || "-",
        "Yönetici Cüzdan Bakiye": row.owner?.wallet?.amount ?? "-",
        "Yönetici Avatar Dosya Adı": row.owner?.avatar?.name || "-",
        "Yönetici Avatar Uzantı": row.owner?.avatar?.extension || "-",
        "Yönetici Avatar URL": row.owner?.avatar ? getMediaURL(row.owner.avatar) : "-",
        "Shop Logo URL": row.logo ? getMediaURL(row.logo) : "-",
        "Shop Banner URL": row.banner ? getMediaURL(row.banner) : "-",
      }));

      if (!exportRows.length) {
        enqueueSnackbar("Export için kayıt bulunamadı.", { variant: "warning" });
        return;
      }

      const worksheet = XLSX.utils.json_to_sheet(exportRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Shops");
      XLSX.writeFile(workbook, `shops-export-${Date.now()}.xlsx`);
      enqueueSnackbar("XLSX export hazırlandı.", { variant: "success" });
    } catch (error) {
      console.log(error.response || error);
      enqueueSnackbar("XLSX export alınamadı.", { variant: "error" });
    } finally {
      setExporting(false);
    }
  };

  const tableKey = `${filters.transfer_by}|${filters.status}|${filters.industry_id}`;

  useEffect(() => {
    scrollTop();
  }, []);

  return (
    <>
      {loading ?
        "Loading"
      : <div className="main-table">
          <Box sx={{ mb: 2 }}>
            <Collapse in={showFilters}>
              <Box
                sx={{
                  p: 2,
                  border: "1px solid #e0e0e0",
                  borderRadius: 2,
                  backgroundColor: "#fff",
                  display: "grid",
                  gap: 1,
                  gridTemplateColumns: { xs: "1fr", md: "repeat(4, 1fr)" },
                }}
              >
                <RayaField
                  select
                  fullWidth
                  label={t("transferBy")}
                  value={filters.transfer_by}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      transfer_by: e.target.value,
                    }))
                  }
                >
                  <MenuItem value="all">{t("all")}</MenuItem>
                  <MenuItem value="system">kolaydoy</MenuItem>
                  <MenuItem value="shop">kendisi</MenuItem>
                </RayaField>
                <RayaField
                  select
                  fullWidth
                  label={t("industry")}
                  value={filters.industry_id}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      industry_id: e.target.value,
                    }))
                  }
                >
                  <MenuItem value="all">{t("all")}</MenuItem>
                  {industries.map((industry) => (
                    <MenuItem key={industry.id} value={industry.id}>
                      {t(industry.name)}
                    </MenuItem>
                  ))}
                </RayaField>
                <RayaField
                  select
                  fullWidth
                  label={t("status")}
                  value={filters.status}
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, status: e.target.value }))
                  }
                >
                  <MenuItem value="all">{t("all")}</MenuItem>
                  <MenuItem value="active">{t("active")}</MenuItem>
                  <MenuItem value="pending">{t("pending")}</MenuItem>
                  <MenuItem value="inactive">{t("inactive")}</MenuItem>
                </RayaField>
                <Typography
                  component="span"
                  onClick={!exporting ? handleExportXlsx : undefined}
                  sx={{
                    height: 56,
                    minWidth: 130,
                    justifySelf: { xs: "stretch", md: "end" },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: { xs: "flex-start", md: "flex-end" },
                    color: exporting ? "text.disabled" : "#2e7d32",
                    fontWeight: 700,
                    cursor: exporting ? "not-allowed" : "pointer",
                    userSelect: "none",
                  }}
                >
                  {exporting ? "Exporting..." : "Export XLSX"}
                </Typography>
              </Box>
            </Collapse>
          </Box>
          <MaterialTable
            key={tableKey}
            tableRef={tableRef}
            icons={tableIcons}
            title={
              <>
                <Link to="/panel/manage/shops/add">
                  <Hidden mdDown>
                    <Button
                      variant="contained"
                      color="secondary"
                      startIcon={<AddBox />}
                    >
                      {t("makeShop")}
                    </Button>
                  </Hidden>
                  <Hidden mdUp>
                    <IconButton color="primary">
                      <AddBox />
                    </IconButton>
                  </Hidden>
                </Link>
              </>
            }
            localization={getDataTableLocalization()}
            columns={columns}
            components={{
              Toolbar: (props) => (
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Box sx={{ flex: 1 }}>
                    <MTableToolbar {...props} />
                  </Box>
                  <Button
                    variant={showFilters ? "contained" : "outlined"}
                    color="primary"
                    onClick={() => setShowFilters((prev) => !prev)}
                    sx={{ mr: 2 }}
                  >
                    Filter
                  </Button>
                </Box>
              ),
            }}
            data={(query) =>
              new Promise((resolve, reject) => {
                searchRef.current = query.search || "";
                axios
                  .get(`${defaultVariables.url}/shops/quickView`, {
                    headers: {
                      Accept: "application/json",
                      Authorization: `Bearer ${localStorage.getItem("ACT")}`,
                    },
                    params: {
                      page: query.page + 1,
                      per_page: query.pageSize,
                      search: query.search,
                      status:
                        filters.status !== "all" ? filters.status : undefined,
                      transfer_by:
                        filters.transfer_by !== "all" ?
                          filters.transfer_by
                        : undefined,
                      industry_id:
                        filters.industry_id !== "all" ?
                          filters.industry_id
                        : undefined,
                    },
                  })
                  .then((response) => {
                    const data = response.data.data.map((row) => ({
                      id: row.id,
                      name: row.name,
                      industry: t(row.industry?.name),
                      logo: getMediaURL(row.logo, "small"),
                      created_at: row.created_at,
                      status: row.status,
                      wallet_id: row.owner?.wallet?.id,
                      transfer_by: row.transfer_by,
                      commission: row.commission,
                    }));
                    resolve({
                      data: data,
                      page: response.data.current_page - 1,
                      totalCount: response.data.total,
                    });
                  })
                  .catch((error) => console.log(error.response));
              })
            }
            options={{
              pageSize: TablePage,
              pageSizeOptions: TableCount,
              defaultExpanded: false,
              draggable: false,
              search: true,
              searchFieldAlignment: "right",
            }}
            editable={{
              onRowDelete: (oldData) =>
                new Promise((resolve) => {
                  setTimeout(() => {
                    resolve();
                    onDelete(oldData.id);
                    setData((prevData) =>
                      prevData.filter((item) => item.id !== oldData.id),
                    );
                  }, 600);
                }),
            }}
          />
        </div>
      }
    </>
  );
};
export default withStyles(Styles)(Shops);
